import { useCallback, useEffect, useRef, useState } from 'react';
import Timeline from '@mui/lab/Timeline';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { type AuditLogEntry } from '@core/api/auditLog';
import { renderAuditLogSummary } from '@core/audit';
import { ActionButton, AuditLogChangesTable, Dialog } from '@core/components';
import { getOrderAuditLogs } from '../../../api/order';
import type { Order } from '../../../api/order';
import { HistoryTimelineRow } from './HistoryTimelineRow';
import { historyGapLabel, orderHistoryChanges } from './historyTimeline';

interface OrderHistoryTimelineProps {
  order: Order;
  refreshKey?: number;
}

const PAGE_SIZE = 50;

const OrderHistoryTimeline = ({
  order,
  refreshKey = 0,
}: OrderHistoryTimelineProps) => {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [detailEntry, setDetailEntry] = useState<AuditLogEntry | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadFailed(false);
    setLoadMoreFailed(false);
    getOrderAuditLogs(order.id, { limit: PAGE_SIZE, page: 1 })
      .then((response) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setEntries(response.items);
        setTotal(response.total);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setEntries([]);
        setTotal(0);
        setLoadFailed(true);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setLoading(false);
      });
  }, [order.id, refreshKey, retryKey]);

  const loadEarlier = useCallback(async () => {
    const requestId = requestIdRef.current;
    const nextPage = Math.floor(entries.length / PAGE_SIZE) + 1;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    try {
      const response = await getOrderAuditLogs(order.id, {
        limit: PAGE_SIZE,
        page: nextPage,
      });
      if (requestId !== requestIdRef.current) {
        return;
      }
      setEntries((current) => {
        const seen = new Set(current.map((entry) => entry.id));
        return [
          ...current,
          ...response.items.filter((entry) => !seen.has(entry.id)),
        ];
      });
      setTotal(response.total);
    } catch {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setLoadMoreFailed(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }, [entries.length, order.id]);

  const detailChanges = detailEntry ? orderHistoryChanges(detailEntry) : null;
  const hasEarlier = entries.length < total;

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 2 }}
        >
          Order History
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : loadFailed ? (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Couldn&apos;t load order history.
            </Typography>
            <ActionButton
              variant="outline"
              size="sm"
              onClick={() => setRetryKey((key) => key + 1)}
              sx={{ mt: 1 }}
            >
              Retry
            </ActionButton>
          </Box>
        ) : entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No history recorded for this order yet.
          </Typography>
        ) : (
          <>
            <Timeline
              position="right"
              sx={{
                m: 0,
                p: 0,
                [`& .MuiTimelineItem-root:before`]: { flex: 0, padding: 0 },
              }}
            >
              {entries.map((entry, index) => {
                const next = entries[index + 1];
                return (
                  <HistoryTimelineRow
                    key={entry.id}
                    entry={entry}
                    gapLabel={
                      next
                        ? historyGapLabel(
                            new Date(entry.createdAt),
                            new Date(next.createdAt)
                          )
                        : null
                    }
                    isLatest={entry.id === entries[0]?.id}
                    showConnector={index < entries.length - 1}
                    onOpen={setDetailEntry}
                  />
                );
              })}
            </Timeline>
            {hasEarlier ? (
              <Box sx={{ mt: 1 }}>
                {loadMoreFailed ? (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    Couldn&apos;t load earlier history.
                  </Typography>
                ) : null}
                <ActionButton
                  variant="outline"
                  size="sm"
                  loading={loadingMore}
                  onClick={loadEarlier}
                >
                  Show earlier
                </ActionButton>
              </Box>
            ) : null}
          </>
        )}
      </Paper>
      <Dialog
        open={detailEntry !== null}
        onClose={() => setDetailEntry(null)}
        title={
          detailEntry != null && (
            <Box component="span">{renderAuditLogSummary(detailEntry)}</Box>
          )
        }
        submitText="Close"
        onSubmitClick={() => setDetailEntry(null)}
        hideCancel
        maxWidth="md"
      >
        {detailChanges ? (
          <AuditLogChangesTable changes={detailChanges} />
        ) : null}
      </Dialog>
    </>
  );
};

export default OrderHistoryTimeline;
